// ============================================================================
// avecMesCache: o que o sync mensal de 60 s grava em fk_avec_cache (A1.5 do
// "Dashboard sem zero silencioso", 23/10/2026). Puro (sem React), testável.
// ============================================================================
// mergeMaxWins veio do AvecSyncContext SEM mudança nenhuma (mesmo código).
// prepararMesParaGravar = o passo do doSync:
//   base = force ? data : mergeMaxWins(prev, data)
//   com o estado da coleta: protegerMes(prev, base) → relatório que falhou herda
//   o valor da coleta anterior do mesmo mês (no mês corrente a cadeia para em
//   12 h; no mês fechado não tem limite) e o que continua faltando SEM valor
//   anterior volta a null (o Math.max do merge transformava a falha sem
//   anterior em 0); o número que o merge manteve do anterior fica, como antes.
//   Sem falha nenhuma o objeto é o de hoje.
// ============================================================================
import { protegerMes } from './avecFalhas.js';

// Merge MAX-WINS: pra mês corrente, KPIs acumulativos só podem SUBIR
// (faturamento, visitas, comissões etc só somam ao longo do mês). Se uma
// resposta parcial da API trouxer valor MENOR, descarta esse campo.
// Evita oscilação ("vai e volta") quando a API falha intermitentemente.
//
// NÃO incluir aqui campos que podem LEGITIMAMENTE CAIR durante o mês:
//   - agendamentosPreferenciais / semPreferencia: contagem de agendamentos.
//     Se um cliente cancela, o número CAI. MAX-wins prenderia o valor de pico
//     pra sempre (ex: 8 marcados de manhã, 3 cancelaram → API retorna 5,
//     mas MAX-wins manteria 8). Pra esses campos usamos o último valor.
export const ACCUMULATIVE_FIELDS = [
  'faturamento', 'visitas', 'clientesUnicos', 'clientesNovos',
  'servicosRealizados', 'produtosVendidos', 'faturamentoProdutos',
  'pacotesVendidosTotal',
  'valorPacotes', 'comandasServico', 'comandasProdutoServico', 'comandasProduto',
];

export function mergeMaxWins(prev, next) {
  if (!prev) return next;
  if (!next || !next.units) return prev;
  const merged = { ...next, units: { ...next.units } };
  for (const [unitKey, nextUnit] of Object.entries(next.units || {})) {
    const prevUnit = prev.units?.[unitKey];
    if (!prevUnit) continue;
    // Se nextUnit veio com erro, mantém prev
    if (nextUnit._error) {
      merged.units[unitKey] = prevUnit;
      continue;
    }
    // Detecta "queda absurda" (mais de 30% pra baixo no faturamento)
    // Indica chamada parcial/falha da API. Mantém prev inteiro.
    const prevFat = Number(prevUnit.faturamento) || 0;
    const nextFat = Number(nextUnit.faturamento) || 0;
    if (prevFat > 5000 && nextFat < prevFat * 0.7) {
      merged.units[unitKey] = prevUnit;
      continue;
    }
    // Merge campo a campo: pega MAX entre prev e next pros acumulativos
    const mergedUnit = { ...nextUnit };
    for (const field of ACCUMULATIVE_FIELDS) {
      const pv = Number(prevUnit[field]) || 0;
      const nv = Number(nextUnit[field]) || 0;
      mergedUnit[field] = Math.max(pv, nv);
    }
    // Re-deriva ticket médio e tração (não dá pra "max" eles)
    if (mergedUnit.visitas > 0) {
      mergedUnit.ticketMedio = mergedUnit.faturamento / mergedUnit.visitas;
      mergedUnit.tracaoVendas = mergedUnit.servicosRealizados / mergedUnit.visitas;
    }
    // Preserva extras antigos se novos vieram vazios
    if ((!nextUnit.extras || nextUnit.extras.length === 0) && prevUnit.extras?.length > 0) {
      mergedUnit.extras = prevUnit.extras;
    }
    // Preserva COMISSÕES antigas se as novas vieram vazias — mesma proteção
    // dos extras: resposta parcial do 0123 zerava a comissão da unidade
    // inteira até a próxima resposta boa (caso Moema ago/2026)
    const prevComissoes = prevUnit.profissionais?.comissoes || [];
    const nextComissoes = nextUnit.profissionais?.comissoes || [];
    if (nextComissoes.length === 0 && prevComissoes.length > 0) {
      mergedUnit.profissionais = {
        ...(mergedUnit.profissionais || {}),
        comissoes: prevComissoes,
      };
    }
    // Preserva profissionais antigos se novos vieram com menos profs ou vazios
    const prevProfList = prevUnit.profissionais?.faturamento || [];
    const nextProfList = nextUnit.profissionais?.faturamento || [];
    if (prevProfList.length > nextProfList.length * 1.3) {
      mergedUnit.profissionais = prevUnit.profissionais;
    }
    // Preserva pacotesPorProfissional antigos se novos estão menores
    const prevPkgsByProf = prevUnit.profissionais?.pacotesPorProfissional || {};
    const nextPkgsByProf = nextUnit.profissionais?.pacotesPorProfissional || {};
    if (Object.keys(prevPkgsByProf).length > Object.keys(nextPkgsByProf).length) {
      mergedUnit.profissionais = {
        ...mergedUnit.profissionais,
        pacotesPorProfissional: { ...prevPkgsByProf, ...nextPkgsByProf },
      };
    }
    merged.units[unitKey] = mergedUnit;
  }
  return merged;
}

// Mês já fechado (anterior ao mês de `agora`, no relógio do aparelho)?
export function ehMesFechado(year, month, agora = Date.now()) {
  const y = Number(year), m = Number(month);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return false;
  const d = new Date(agora);
  return y < d.getFullYear() || (y === d.getFullYear() && m < d.getMonth() + 1);
}

// Revisão 23/10/2026: mês fechado herda sem limite de idade (achado 10) e o
// ticket recalculado segue a régua do caminho que gerou o objeto (achado 5).
export function prepararMesParaGravar(prev, data, { force = false, coletaEstado = false, agora = Date.now(), mesFechado } = {}) {
  const base = force ? data : mergeMaxWins(prev, data);
  if (!coletaEstado) return base;
  const fechado = mesFechado ?? ehMesFechado(data?._year, data?._month, agora);
  return protegerMes(prev, base, { agora, mesFechado: fechado, regraMaxWins: !force && !!prev });
}
